// useDiaryReorder — local order state on top of the server's DiaryDay list.
//
// Tracks per-day order independently from the server, exposes a moveBooking
// mutator (used by the dnd-kit drop handler), and computes hasUnsavedChanges
// + changedDayCount. saveOrder() PATCHes /diary/reorder with the
// (unique_id=enc_id, display_order) tuples for every changed day's rows;
// resetOrder() drops local edits.
//
// Server reorder endpoint (per server/HANDOFF.md + bookings.types.ts):
//   PATCH /api/v1/collections/diary/reorder
//   body: { orders: [{ unique_id: <enc_id>, display_order: number }] }
// The server's `decryptUniqueIdFromUrl(unique_id)` step means the client
// sends the opaque enc_id verbatim — which is exactly what BookingSummary.enc_id
// already provides (Decision 3.10).

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';
import type { BookingSummary, DiaryDay, DiaryReorderInput } from '../../../types/api';

export interface UseDiaryReorderParams {
  driverId: number;
  weekStart: string;
  /** Server-supplied days; the source of truth before local edits. */
  serverDays: DiaryDay[] | undefined;
}

export interface UseDiaryReorderApi {
  /** Days with the user's local order applied; falls back to server order. */
  orderedDays: DiaryDay[];
  hasUnsavedChanges: boolean;
  changedDayCount: number;
  /** Day-of-week labels for every day with a pending local-order edit, in
   *  week order. SaveOrderBar uses this to phrase per-day vs aggregate
   *  status. */
  changedDayNames: string[];
  /** Days with pending local-order edits, in week order. */
  changedDayDates: string[];
  /** Move a booking inside a single day. Returns true if the order changed. */
  moveBooking: (date: string, fromIndex: number, toIndex: number) => boolean;
  saveOrder: () => Promise<void>;
  resetOrder: () => void;
  /** True while a save is in flight. */
  isSaving: boolean;
}

interface OverrideMap {
  /** Map of day-date → ordered enc_id list. Empty when no local edits. */
  [dayDate: string]: string[];
}

function bookingOrder(day: DiaryDay): string[] {
  return day.bookings.map((b) => b.enc_id);
}

function applyOverride(
  day: DiaryDay,
  override: string[] | undefined,
): DiaryDay {
  if (!override) return day;
  const byEnc = new Map(day.bookings.map((b) => [b.enc_id, b]));
  const reordered: BookingSummary[] = [];
  for (const encId of override) {
    const b = byEnc.get(encId);
    if (b) reordered.push(b);
  }
  // Append any rows the server has that the override does not (defensive
  // — should not happen unless the server delivered a new row mid-edit).
  for (const b of day.bookings) {
    if (!override.includes(b.enc_id)) reordered.push(b);
  }
  return { ...day, bookings: reordered };
}

function arraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) if (a[i] !== b[i]) return false;
  return true;
}

export function useDiaryReorder({
  driverId,
  weekStart,
  serverDays,
}: UseDiaryReorderParams): UseDiaryReorderApi {
  const queryClient = useQueryClient();
  const [overrides, setOverrides] = useState<OverrideMap>({});
  const [isSaving, setIsSaving] = useState(false);

  // Reset overrides whenever the driver/week changes — switching context
  // discards local edits silently. The page guards navigation separately.
  useEffect(() => {
    setOverrides({});
  }, [driverId, weekStart]);

  // Drop overrides for any day whose enc_id roster no longer matches
  // (e.g. server delivered a different row count). We do NOT auto-merge —
  // simpler to revert that day's local edit than risk reordering rows the
  // user never saw.
  useEffect(() => {
    if (!serverDays || Object.keys(overrides).length === 0) return;
    let changed = false;
    const next: OverrideMap = { ...overrides };
    for (const day of serverDays) {
      const local = next[day.date];
      if (!local) continue;
      const sortedLocal = [...local].sort();
      const sortedServer = bookingOrder(day).sort();
      if (!arraysEqual(sortedLocal, sortedServer)) {
        delete next[day.date];
        changed = true;
      }
    }
    if (changed) setOverrides(next);
  }, [serverDays, overrides]);

  const orderedDays = useMemo<DiaryDay[]>(() => {
    if (!serverDays) return [];
    return serverDays.map((d) => applyOverride(d, overrides[d.date]));
  }, [serverDays, overrides]);

  const { changedDayCount, changedDayNames, changedDayDates } = useMemo(() => {
    const names: string[] = [];
    const dates: string[] = [];
    if (!serverDays) {
      return { changedDayCount: 0, changedDayNames: names, changedDayDates: dates };
    }
    for (const day of serverDays) {
      const override = overrides[day.date];
      if (!override) continue;
      if (!arraysEqual(override, bookingOrder(day))) {
        names.push(day.day_of_week);
        dates.push(day.date);
      }
    }
    return { changedDayCount: names.length, changedDayNames: names, changedDayDates: dates };
  }, [serverDays, overrides]);

  const hasUnsavedChanges = changedDayCount > 0;

  const moveBooking = useCallback(
    (date: string, fromIndex: number, toIndex: number): boolean => {
      if (!serverDays) return false;
      const day = serverDays.find((d) => d.date === date);
      if (!day) return false;
      const current = overrides[date] ?? bookingOrder(day);
      if (
        fromIndex < 0 ||
        toIndex < 0 ||
        fromIndex >= current.length ||
        toIndex >= current.length ||
        fromIndex === toIndex
      ) {
        return false;
      }
      const next = [...current];
      const [moved] = next.splice(fromIndex, 1);
      if (!moved) return false;
      next.splice(toIndex, 0, moved);
      setOverrides((prev) => ({ ...prev, [date]: next }));
      return true;
    },
    [serverDays, overrides],
  );

  const resetOrder = useCallback(() => {
    setOverrides({});
  }, []);

  const saveOrder = useCallback(async (): Promise<void> => {
    if (!serverDays || !hasUnsavedChanges) return;
    setIsSaving(true);
    try {
      const items: { unique_id: string; display_order: number }[] = [];
      for (const day of serverDays) {
        const override = overrides[day.date];
        if (!override) continue;
        if (arraysEqual(override, bookingOrder(day))) continue;
        override.forEach((encId, idx) => {
          items.push({ unique_id: encId, display_order: idx });
        });
      }
      if (items.length === 0) return;
      const body: DiaryReorderInput = { orders: items };
      await apiClient.patch(endpoints.diary.reorder, body);
      // On success, clear overrides so the server's fresh order becomes the
      // canonical view, then invalidate the week to refetch.
      setOverrides({});
      await queryClient.invalidateQueries({
        queryKey: queryKeys.diary.week(driverId, weekStart),
      });
    } finally {
      setIsSaving(false);
    }
  }, [serverDays, overrides, hasUnsavedChanges, driverId, weekStart, queryClient]);

  return {
    orderedDays,
    hasUnsavedChanges,
    changedDayCount,
    changedDayNames,
    changedDayDates,
    moveBooking,
    saveOrder,
    resetOrder,
    isSaving,
  };
}
